import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
} from 'lucide-react';

/**
 * Build the visible page-number buttons (1, 2, 3, ..., N pattern with
 * ellipses + start/end anchors). Extracted from the inline IIFE in
 * DataTable.js so it's unit-testable.
 */
function buildPageButtons({ currentPage, totalPages, goToPage, testIdPrefix }) {
  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }

  if (start > 1) {
    pages.push(
      <Button
        key={1}
        variant={currentPage === 1 ? "default" : "outline"}
        size="sm"
        onClick={() => goToPage(1)}
        className="h-8 w-8 p-0"
      >
        1
      </Button>
    );
    if (start > 2) {
      pages.push(<span key="start-ellipsis" className="px-1 text-slate-400">...</span>);
    }
  }

  for (let i = start; i <= end; i++) {
    if (i === 1 && start > 1) continue;
    if (i === totalPages && end < totalPages) continue;
    pages.push(
      <Button
        key={i}
        variant={currentPage === i ? "default" : "outline"}
        size="sm"
        onClick={() => goToPage(i)}
        className="h-8 w-8 p-0"
        data-testid={`${testIdPrefix}-page-${i}`}
      >
        {i}
      </Button>
    );
  }

  if (end < totalPages) {
    if (end < totalPages - 1) {
      pages.push(<span key="end-ellipsis" className="px-1 text-slate-400">...</span>);
    }
    pages.push(
      <Button
        key={totalPages}
        variant={currentPage === totalPages ? "default" : "outline"}
        size="sm"
        onClick={() => goToPage(totalPages)}
        className="h-8 w-8 p-0"
      >
        {totalPages}
      </Button>
    );
  }

  return pages;
}

/**
 * Bottom pagination strip: page-size selector, "Showing X to Y of N",
 * and the page-navigation cluster. Pure presentational — caller owns
 * state.
 */
const DataTablePagination = ({
  pageSize,
  pageSizeOptions,
  handlePageSizeChange,
  startRecord,
  endRecord,
  totalRecords,
  currentPage,
  totalPages,
  goToPage,
  testIdPrefix,
}) => {
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2">
      {/* Page Size Selector */}
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <span>Show</span>
        <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
          <SelectTrigger className="w-[70px] h-8" data-testid={`${testIdPrefix}-page-size`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map(size => (
              <SelectItem key={size} value={size.toString()}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span>entries</span>
      </div>

      {/* Record Count */}
      <div className="text-sm text-slate-500">
        Showing {startRecord} to {endRecord} of {totalRecords} entries
      </div>

      {/* Page Navigation */}
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(1)}
          disabled={currentPage === 1}
          className="h-8 w-8 p-0"
          data-testid={`${testIdPrefix}-first-page`}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-8 w-8 p-0"
          data-testid={`${testIdPrefix}-prev-page`}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-1">
          {buildPageButtons({ currentPage, totalPages, goToPage, testIdPrefix })}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-8 w-8 p-0"
          data-testid={`${testIdPrefix}-next-page`}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(totalPages)}
          disabled={currentPage === totalPages}
          className="h-8 w-8 p-0"
          data-testid={`${testIdPrefix}-last-page`}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default DataTablePagination;
